import { Modal } from "@dropbox/dig-components/modal";
import { Text } from "@dropbox/dig-components/typography";
import { memo, useCallback } from "react";
import styles from "./ConversationListModal.module.css";

import type {
  ConversationGroup,
  ConversationMetadata,
} from "@mirage/service-conversation/types";
import type { ComposeSession } from "@mirage/shared/compose/compose-session";
import { DashSpinner } from "@mirage/shared/dash-spinner";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import { isInsideExtensionSidePanel } from "@mirage/shared/util/webExtensionsHelpers";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { ConversationRow, LegacySessionRow } from "./ConversationListRows";

interface ConversationListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClickConversation: (conversation: ConversationMetadata) => void;
  onDeleteConversation: (conversation: ConversationMetadata) => void;
  sortedLegacySessions: ComposeSession[];
  handleLegacySessionClick: (session: ComposeSession) => void;
  groupedConversations: ConversationGroup[];
  isLoading?: boolean;
  currentServerConversationId?: string;
}
export const ConversationListModal = memo(
  ({
    isOpen,
    onClose,
    onClickConversation,
    onDeleteConversation,
    sortedLegacySessions,
    handleLegacySessionClick,
    groupedConversations,
    isLoading = false,
    currentServerConversationId,
  }: ConversationListModalProps) => {
    const handleClickConversation = useCallback(
      (conversation: ConversationMetadata) => {
        onClickConversation(conversation);
        onClose();
      },
      [onClickConversation, onClose],
    );

    return (
      <Modal
        open={isOpen}
        isCentered
        width={700}
        withCloseButton="close"
        shouldCloseOnOverlayClick
        onRequestClose={onClose}
      >
        <Modal.Header>
          {isEmbedPage() ? (
            <Modal.Title
              size="medium"
              weightVariant="emphasized"
              className={styles.ModalTitleEmbed}
            >
              {i18n.t("assist_conversation_list_embed_modal_title")}
            </Modal.Title>
          ) : (
            <Modal.Title size="medium" className={styles.ModalTitle}>
              {i18n.t("assist_conversation_list_modal_title")}
            </Modal.Title>
          )}
        </Modal.Header>
        <Modal.Body
          className={classNames(styles.ModalBody, {
            [styles.isExtension]: isInsideExtensionSidePanel(),
          })}
        >
          {isLoading ? (
            <div className={styles.LoadingContainer}>
              <DashSpinner size={40} displayLoadingMessage={false} />
              <Text
                variant="label"
                size="small"
                color="subtle"
                className={styles.LoadingText}
              >
                {i18n.t("loading")}
              </Text>
            </div>
          ) : (
            <>
              {groupedConversations.map((group) => (
                <div key={group.title} className={styles.ConversationGroup}>
                  <Text
                    variant="label"
                    className={classNames(styles.ConversationGroupTitle, {
                      [styles.ConversationGroupTitleEmbed]: isEmbedPage(),
                    })}
                    isBold
                    size="small"
                    tagName="h2"
                    color="subtle"
                  >
                    {group.title}
                  </Text>
                  <div
                    className={classNames(styles.ConversationList, {
                      [styles.ConversationListEmbed]: isEmbedPage(),
                    })}
                  >
                    {group.conversations.map((conversation) => (
                      <ConversationRow
                        key={conversation.serverConversationId}
                        conversation={conversation}
                        isSelected={
                          conversation.serverConversationId ===
                          currentServerConversationId
                        }
                        onClickConversation={handleClickConversation}
                        onDeleteConversation={onDeleteConversation}
                        largeDisplay
                      />
                    ))}
                  </div>
                </div>
              ))}
              {/* Legacy Sessions Group */}
              {sortedLegacySessions.length > 0 && (
                <div className={styles.ConversationGroup}>
                  <Text
                    variant="label"
                    className={classNames(styles.ConversationGroupTitle, {
                      [styles.ConversationGroupTitleEmbed]: isEmbedPage(),
                    })}
                    isBold
                    size="small"
                    tagName="h2"
                    color="subtle"
                  >
                    {i18n.t("older_than_30_days")}
                  </Text>
                  <div
                    className={classNames(styles.ConversationList, {
                      [styles.ConversationListEmbed]: isEmbedPage(),
                    })}
                  >
                    {sortedLegacySessions.map((session: ComposeSession) => (
                      <LegacySessionRow
                        key={session.id}
                        session={session}
                        onClickSession={handleLegacySessionClick}
                        largeDisplay
                        onClose={onClose}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    );
  },
);
ConversationListModal.displayName = "ConversationListModal";
